<template>
    <section class="ComponentExamples__section LabelBadgeExample">
        <div class="LabelBadgeExample__header">
            <div>
                <h2 class="LabelBadgeExample__title">
                    LabelBadge
                </h2>
                <p class="LabelBadgeExample__description">
                    Variants: fill, gradient · Colors: info, success, warning, danger
                </p>
            </div>
            <div class="LabelBadgeExample__toggle">
                <button
                    class="LabelBadgeExample__toggleBtn"
                    :class="{ 'LabelBadgeExample__toggleBtn--active': !showPlayground }"
                    @click="showPlayground = false"
                >
                    Gallery
                </button>
                <button
                    class="LabelBadgeExample__toggleBtn"
                    :class="{ 'LabelBadgeExample__toggleBtn--active': showPlayground }"
                    @click="showPlayground = true"
                >
                    Playground
                </button>
            </div>
        </div>

        <template v-if="!showPlayground">
            <table class="LabelBadgeExample__table">
                <thead>
                    <tr>
                        <th class="LabelBadgeExample__th LabelBadgeExample__th--variant" />
                        <th
                            v-for="color in colors"
                            :key="color"
                            class="LabelBadgeExample__th"
                        >
                            {{ color }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="badgeVariant in badgeVariants"
                        :key="badgeVariant"
                        class="LabelBadgeExample__tr"
                    >
                        <td class="LabelBadgeExample__td LabelBadgeExample__td--label">
                            {{ badgeVariant }}
                        </td>
                        <td
                            v-for="color in colors"
                            :key="color"
                            class="LabelBadgeExample__td"
                        >
                            <span
                                v-if="!isBadgeSupported(badgeVariant, color)"
                                class="LabelBadgeExample__unsupported"
                            >
                                Unsupported
                            </span>
                            <LabelBadge
                                v-else
                                :color="color"
                                :variant="badgeVariant"
                            >
                                {{ color }}
                            </LabelBadge>
                        </td>
                    </tr>
                </tbody>
            </table>

            <h3 class="LabelBadgeExample__subsectionTitle">
                Sizes
            </h3>
            <div class="LabelBadgeExample__sizeTable">
                <table class="LabelBadgeExample__table">
                    <thead>
                        <tr>
                            <th class="LabelBadgeExample__th">
                                Size
                            </th>
                            <th
                                v-for="badgeVariant in badgeVariants"
                                :key="badgeVariant"
                                class="LabelBadgeExample__th"
                            >
                                {{ badgeVariant }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="size in sizes"
                            :key="size"
                            class="LabelBadgeExample__tr"
                        >
                            <td class="LabelBadgeExample__td LabelBadgeExample__td--label">
                                {{ size }}
                            </td>
                            <td
                                v-for="badgeVariant in badgeVariants"
                                :key="badgeVariant"
                                class="LabelBadgeExample__td"
                            >
                                <LabelBadge color="success" :variant="badgeVariant" :size="size">
                                    {{ badgeVariant }}
                                </LabelBadge>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <ComponentPlayground
            v-else
            :component="LabelBadge"
            :defaults="playgroundDefaults"
            :prop-custom-type-labels="{
                variant: 'string => fill | gradient',
                color: 'string => info | success | warning | danger',
                size: 'string => lg | default | sm | xs',
            }"
            description="Interactive playground — edit variant, color, and size to see live changes."
        >
            <template #default="{ liveProps }">
                <LabelBadge v-bind="liveProps">
                    {{ liveProps.color }}
                </LabelBadge>
            </template>
        </ComponentPlayground>
    </section>
</template>

<style lang="scss">
.LabelBadgeExample {
    padding: 1.5rem;
    max-width: 56rem;
    background: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    &__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
    }

    &__title {
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--eidTechGray);
        margin: 0 0 0.25rem;
    }

    &__description {
        font-size: 0.875rem;
        color: var(--eidTechGray-75);
        margin: 0;
    }

    &__toggle {
        display: flex;
        flex-shrink: 0;
        border: 1px solid var(--eidTechGray-25);
        border-radius: 0.375rem;
        overflow: hidden;
    }

    &__toggleBtn {
        padding: 0.3rem 0.75rem;
        font-size: 0.8125rem;
        font-weight: 500;
        background: none;
        border: none;
        cursor: pointer;
        color: var(--eidTechGray-75);

        &:hover:not(&--active) {
            background: var(--eidTechGray-10);
        }

        &--active {
            background: var(--eidTechGray-10);
            color: var(--eidTechGray);
        }
    }

    &__unsupported {
        display: inline-flex;
        align-items: center;
        padding: 0.5rem 0.75rem;
        font-size: 13px;
        color: var(--eidTechGray-75);
        font-style: italic;
    }

    &__subsectionTitle {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        color: var(--eidTechGray);
    }

    &__sizeTable {
        margin-top: 0;
    }

    &__table {
        border-collapse: collapse;
    }

    &__th {
        padding: 6px 24px 6px 0;
        font-size: 13px;
        font-weight: 500;
        color: var(--eidTechGray-75);
        text-align: left;

        &--variant {
            width: 5rem;
        }
    }

    &__tr {
        border-top: 1px solid var(--eidTechGray-10);
    }

    &__td {
        padding: 10px 24px 10px 0;
        vertical-align: middle;

        &--label {
            font-size: 13px;
            font-weight: 500;
            color: var(--eidTechGray-75);
            font-family: monospace;
            white-space: nowrap;
            min-width: 5rem;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { LabelBadge, LabelBadgeColor, LabelBadgeVariant } from '@evidentid/dashboard-commons/components/LabelBadge';
    import { ComponentSize } from '@evidentid/tprm-portal-lib/models/common';

    import ComponentPlayground from './ComponentPlayground.vue';

    const BADGE_VARIANTS = [ 'fill', 'gradient' ] as const;
    const COLORS = [ 'info', 'success', 'warning', 'danger' ] as const;
    const SIZES = [ 'lg', 'default', 'sm', 'xs' ] as const;
    const UNSUPPORTED_BADGES: Record<string, Record<string, boolean>> = {
        gradient: { info: true, warning: true, danger: true },
    };

    export default defineComponent({
        name: 'LabelBadgeExample',
        components: { LabelBadge, ComponentPlayground },
        data() {
            return {
                LabelBadge,
                badgeVariants: BADGE_VARIANTS,
                colors: COLORS,
                sizes: SIZES,
                showPlayground: false,
                playgroundDefaults: {
                    variant: 'fill' as LabelBadgeVariant,
                    color: 'info' as LabelBadgeColor,
                    size: 'default' as ComponentSize,
                },
            };
        },
        methods: {
            isBadgeSupported(variant: string, color: string): boolean {
                return !UNSUPPORTED_BADGES[variant]?.[color];
            },
        },
    });
</script>
